/**
 * OnboardingInsurance.jsx — v228.2.0 (Phase 2a)
 *
 * Step 3 of 8. Guided coverage sequence, all auto-firing:
 *
 *   add coverage → DISCOVERY (proactive) → card + info (manual fallback) → ELIGIBILITY
 *
 * Approach A (partial saves): the discovery + eligibility endpoints read the
 * patient's data server-side, but onboarding is otherwise in-memory until the
 * end. So we persist demographics on entry (so discovery has name + DOB) and
 * persist insurance once coverage is set (so eligibility has the member ID),
 * right before each Stedi call. The final buildPayload carries discovery_result
 * + last_eligibility so the end-of-onboarding submit never clobbers them.
 *
 * Reuses the proven EligibilityBadge + the runPracticeEligibility flow already
 * shipped in Profile → Insurance, and the discovery candidate shape
 * { payerName, carrierId, memberIdGuess } from the Stedi discovery seam.
 */
import { useState, useMemo, useEffect, useRef } from 'react'
import { OverlayPortal } from '../../components/ModalPortal'
import { Camera, Check, CheckCircle, Search, X, Shield, Sparkles, Info } from 'lucide-react'
import DobPicker from '../../components/DobPicker'
import { useI18n } from '../../i18n'
import { useOnboardingStore, useUserStore } from '../../stores'
import { profileAPI, insuranceRegistryAPI } from '../../services/api'
import CardScanner from '../../components/CardScanner'
import useCardScanPair from '../../hooks/useCardScanPair'
import { processCardFile, persistCardData } from '../../utils/processCardImage'
import { cardUploadFailureMessage } from '../../utils/uploadRefusal'
import EligibilityBadge from '../../components/EligibilityBadge'
import InsuranceCandidateList from '../../components/InsuranceCandidateList'
import { payerPick, candidatePayerPick, payerKeysForWire, regionChoices, regionPick, badgeHiddenByRegionCard, regionSettled } from '../../utils/payerPick'
import { buildDiscoveredSecondary, secondaryFromResponse } from '../../utils/insuranceDiscovery'
import { eligibilityAnswer, eligibilityKey, limitedResultFor, verdictWithheld, coverageAlreadyChecked, storedResultFor, inFlightRun, forgetCoverageCheck, loadedResultKey, noticeForCoverage, MEMBER_ID_IDLE_MS } from '../../utils/eligibilityLimit'
import { DISCOVERY_CAP, SEARCH_DEBOUNCE_MS, needsPolicyholder, policyholderComplete, policyholderFields, policyholderForWire, policyholderPending, policyholderSignature, identificationForSearch, discoveryOutcome, outcomeMessageKey } from '../../utils/discoverySearch'
import { INSURANCE_DISCOVERY_ENABLED } from '../../config/featureFlags'
import PrimaryCareDoctor from '../../components/PrimaryCareDoctor' // s106 A0 (D497)
import SelfPayConfirmedNote from '../../components/SelfPayConfirmedNote' // PROD-1132: one owner with the before-share block
import { pcpNeededFor } from '../../utils/pcpChoice' // s106 A0
import {
  OnboardingShell,
  ScreenHeader,
  ScreenIntro,
  PrimaryButton,
  SecondaryButton,
  Footer,
  ScrollBody,
  TextField,
  useScrollToTop,
  NAVY,
  BLUE,
  LIGHT_BLUE,
  ICE_BLUE,
  GREEN,
  PASTEL_GREEN,
  TEXT_MUTED,
  TEXT_HINT,
  BORDER_LIGHT,
  PROFILE_STEPS,
} from './onboardingPrimitives'
import { OrellaSpinner } from '../../components/OrellaLoader' // s123: the app's one spinner

function CardSlot({ captured, onCamera, onFile, onRemove, label, busy }) {
  const { t } = useI18n()
  const inputRef = useRef(null)
  const [drag, setDrag] = useState(false)
  const pick = (e) => { e.stopPropagation(); inputRef.current?.click() }
  const onInput = (e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = '' }
  const onDrop = (e) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer?.files?.[0]; if (f) onFile(f) }
  return (
    <div
      onClick={captured || busy ? undefined : onCamera}
      onDragOver={(e) => { e.preventDefault(); if (!captured && !busy) setDrag(true) }}
      onDragLeave={() => setDrag(false)}
      onDrop={captured || busy ? undefined : onDrop}
      className="flex flex-col items-center justify-center transition"
      style={{
        width: '100%',
        aspectRatio: '1.586 / 1',
        borderRadius: 14,
        background: captured ? PASTEL_GREEN + '40' : (drag ? '#DCEEFF' : ICE_BLUE),
        border: `2px dashed ${captured ? GREEN : (drag ? BLUE : LIGHT_BLUE)}`,
        cursor: captured || busy ? 'default' : 'pointer',
        position: 'relative',
        overflow: 'hidden',
        gap: 2,
      }}
    >
      {captured ? (
        <>
          {(captured.url || captured.data) ? (
            <img src={captured.url || captured.data} alt={label} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : null}
          <div
            className="absolute"
            style={{ top: 8, left: 8, padding: '3px 8px', borderRadius: 999, background: GREEN, color: '#fff', fontSize: 11.5, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 4 }}
          >
            <Check size={12} strokeWidth={3} />
            {label}
          </div>
          {onRemove && (
            <button
              type="button"
              aria-label={`Remove ${label}`}
              onClick={(e) => { e.stopPropagation(); onRemove() }}
              className="absolute"
              style={{ top: 6, right: 6, width: 28, height: 28, borderRadius: 999, border: 'none', background: 'rgba(21,53,79,0.78)', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}
            >
              <X size={16} strokeWidth={2.5} />
            </button>
          )}
          {/* v242.64.0 (BUG-8): RETAKE. Once a side was captured this slot offered
              ONLY the remove button - the container's onClick is disabled while
              `captured` is truthy - so replacing a bad photo meant deleting it first
              and nothing on screen said so. ProfileInsurance has had this affordance
              since v242.37.0 and onboarding, which every new patient meets FIRST, did
              not. Third time this pair of screens has disagreed (ORELLA_RULES.md 5).
              Opens the same CardScanner, which offers camera AND library via onUpload. */}
          <button
            type="button"
            aria-label={`Retake ${label}`}
            onClick={(e) => { e.stopPropagation(); onCamera() }}
            className="absolute"
            style={{ bottom: 6, right: 6, width: 28, height: 28, borderRadius: 999, border: 'none', background: 'rgba(21,53,79,0.78)', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}
          >
            <Camera size={15} strokeWidth={2.5} />
          </button>
        </>
      ) : busy ? (
        <>
          <OrellaSpinner size={22} />
          <span style={{ fontSize: 12.5, fontWeight: 500, color: NAVY, marginTop: 4 }}>{t('cardScanner.reading', 'Reading…')}</span>
        </>
      ) : (
        <>
          <Camera size={26} color={BLUE} />
          <span style={{ fontSize: 14, fontWeight: 600, color: NAVY, marginTop: 2 }}>{label}</span>
          <span style={{ fontSize: 11, color: TEXT_MUTED }}>
            {t('onboarding.takePhoto', 'Take photo')}
          </span>
          <input ref={inputRef} type="file" accept="image/*" onChange={onInput} style={{ display: 'none' }} />
        </>
      )}
    </div>
  )
}

function CarrierSearchSheet({ onPick, onClose }) {
  const { t } = useI18n()
  const [q, setQ] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState(null)

  // v228.3.2 — live search against Stedi's real payer network (no decoy list).
  useEffect(() => {
    const term = q.trim()
    if (term.length < 2) { setResults([]); setSearching(false); return }
    let cancelled = false
    setSearching(true)
    const tmr = setTimeout(async () => {
      try {
        const { payers, error } = await insuranceRegistryAPI.searchPayersDetailed(term)
        if (!cancelled) { setResults(payers || []); setSearchError(error || null) }
      } catch (e) {
        if (!cancelled) { setResults([]); setSearchError((e && e.message) || 'Payer search unavailable') }
      } finally {
        if (!cancelled) setSearching(false)
      }
    }, 300)
    return () => { cancelled = true; clearTimeout(tmr) }
  }, [q])

  return (
    <OverlayPortal>
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      style={{ background: 'rgba(21,53,79,0.45)' }}
      onClick={onClose}
    >
      <div
        className="w-full bg-white"
        style={{
          maxWidth: 460, maxHeight: '78vh',
          borderTopLeftRadius: 22, borderTopRightRadius: 22,
          overflow: 'hidden', display: 'flex', flexDirection: 'column',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2" style={{ padding: '16px 18px', borderBottom: `1px solid ${BORDER_LIGHT}` }}>
          <Search size={18} color={TEXT_HINT} />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('onboarding.insurance.searchPlaceholder', 'Search insurance carrier')}
            className="flex-1 focus:outline-none"
            style={{ fontSize: 15, color: NAVY, border: 'none', background: 'transparent' }}
          />
          {searching && <OrellaSpinner size={16} tone="inherit" />}
          <button
            onClick={onClose}
            className="rounded-full flex items-center justify-center"
            style={{ width: 32, height: 32, background: '#F2F4F7', border: 'none', cursor: 'pointer' }}
            aria-label="Close"
          >
            <X size={16} color={NAVY} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto" style={{ padding: '8px 0' }}>
          {results.map((p, i) => {
            const pid = p.primaryPayerId || p.stediId
            return (
              <button
                key={(p.stediId || '') + (p.primaryPayerId || '') + i}
                onClick={() => { onPick(p); onClose() }}
                className="w-full text-left transition flex items-center justify-between gap-2"
                style={{
                  padding: '14px 18px', fontSize: 15, color: NAVY, background: '#fff',
                  border: 'none', borderBottom: `1px solid ${BORDER_LIGHT}`, cursor: 'pointer',
                }}
              >
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.displayName}</span>
                <span style={{ marginLeft: 8, fontSize: 11, color: TEXT_HINT, fontFamily: 'monospace' }}>{pid}</span>
              </button>
            )
          })}
          {q.trim().length >= 2 && !searching && results.length === 0 && (
            <div style={{ padding: '20px 24px 4px', textAlign: 'center', color: TEXT_MUTED, fontSize: 13.5, lineHeight: 1.5 }}>
              {searchError
                ? t('onboarding.insurance.searchUnavailable', "I can't reach the plan directory right now — type your insurer's name and I'll use exactly what you wrote.")
                : t('onboarding.insurance.noMatches', 'No matches. Type more or pick another.')}
            </div>
          )}
          {/* v242.64.0 (TF-11): the escape hatch Profile → Insurance already had.
              The payer directory is a remote service; when it is unreachable this
              sheet returned NOTHING and the patient had no way to name their own
              insurer — while a wrong auto-match sat in the field behind it. */}
          {q.trim().length >= 2 && !searching && (
            <div style={{ padding: '10px 18px 18px' }}>
              <button
                onClick={() => { onPick({ displayName: q.trim(), stediId: '', primaryPayerId: '' }); onClose() }}
                className="w-full"
                style={{ padding: '12px 14px', borderRadius: 12, background: '#fff', color: BLUE,
                         border: '1.5px dashed rgba(70,168,249,0.45)', fontSize: 13.5, fontWeight: 600, cursor: 'pointer' }}
              >
                {t('onboarding.insurance.useTyped', 'Use what I typed')} — “{q.trim()}”
              </button>
            </div>
          )}
          {q.trim().length < 2 && (
            <div style={{ padding: 24, textAlign: 'center', color: TEXT_MUTED, fontSize: 14 }}>
              {t('onboarding.insurance.searchHint', 'Type your insurer’s name to search.')}
            </div>
          )}
        </div>
      </div>
    </div>
    </OverlayPortal>
  )
}

export default function OnboardingInsurance({ onNext, onBack }) {
  const { t, hasText } = useI18n()
  const insurance = useOnboardingStore((s) => s.insurance)
  const setInsurance = useOnboardingStore((s) => s.setInsurance)
  const applyInsuranceScan = useOnboardingStore((s) => s.applyInsuranceScan)
  const identity = useOnboardingStore((s) => s.identity)
  const userId = useUserStore((s) => s.user?.id)

  const [scannerSide, setScannerSide] = useState(null) // null | 'front' | 'back'
  const [carrierSheetOpen, setCarrierSheetOpen] = useState(false)

  // ----- Discovery (U2-INS, TEST 3.02) -----
  // ★ It no longer starts on entry or on the policyholder tap: every search is paid and the
  //   server holds 3 per insurance. The triggers are runSearch's callers below. The status
  //   starts idle; 'searching' is set by a search that really started.
  const [discoveryStatus, setDiscoveryStatus] = useState('idle')
  // idle|searching|found|none|pending|missing|cap|same|failed|skipped|applied (utils/discoverySearch.js discoveryOutcome)
  const [candidate, setCandidate] = useState(null)
  const [searchesLeft, setSearchesLeft] = useState(() =>
    Number.isInteger(insurance.discovery_result?.searchesLeft) ? insurance.discovery_result.searchesLeft : null)
  const discoveryFiredRef = useRef(false)
  const skippedRef = useRef(false)
  const searchingRef = useRef(false)
  const lastSearchSigRef = useRef(null)

  // v228.8.6: defer auto-fill + payer matching until BOTH card sides are
  // captured. The back's card-printed payer ID wins over the front's brand
  // name — this is what resolves "Health Net" → the correct Stedi payer
  // instead of PAYER_NOT_MAPPED. applyInsuranceScan with no cardData only
  // writes fields (images were already stored on capture).
  const cardPair = useCardScanPair('insurance', (merged) => {
    applyInsuranceScan(merged)
    // ★ U2-INS C1 (U-7): a card that gave both a carrier and a member ID needs no search; the
    //   eligibility effect below runs on it. Any other card searches (one of the triggers).
    const after = useOnboardingStore.getState().insurance
    if (!(after.provider && after.memberId)) runSearch({ auto: true })
  })

  // ----- Auto eligibility (once coverage is set) -----
  // ★ PROD-713 review B-S1: only a stored result checked for THIS coverage (Q-C5, "kein altes
  //   Ergebnis"). A re-mount after a changed member ID drew the old ID's verdict.
  const [eligibility, setEligibility] = useState(() => storedResultFor(useOnboardingStore.getState(), eligibilityKey(insurance)))
  const [eligibilityChecking, setEligibilityChecking] = useState(false)
  // PROD-691 (P4-3): the server's daily-limit answer, shown under the badge (null = none)
  const [limitNotice, setLimitNotice] = useState(null)
  const lastCheckedKeyRef = useRef(null)
  // ★ PROD-696 (P4): true while the patient is TYPING the member ID. A typed ID is checked on
  //   blur or after MEMBER_ID_IDLE_MS with no change, never per keystroke (each check is paid,
  //   and the server limit is 3 in 24 h: three partial IDs would have used them all).
  const memberIdTypedRef = useRef(false)
  // ★ INSURE-2b (P12): the running eligibility PUT, so a secondary save waits for it. Both
  //   read, modify and write the whole insurance column; racing them can drop the secondary.
  const eligibilityRunRef = useRef(null)
  // ★ PROD-696 follow-up (P4): settles when runEligibility's PUT /profile is answered (or failed).
  //   Continue hands it to OnboardingFlow's section save, which sends its own insurance PUT only
  //   after it: tapping Continue straight from the member-ID field blurs it first, so the blur
  //   check and the section save leave in ONE gesture, and two concurrent whole-column writes can
  //   drop the section's completed:true. It never waits for the paid check itself.
  const eligibilityPutRef = useRef(null)
  const lastPrimaryIdxRef = useRef(null)
  const secondaryIdxRef = useRef(null)
  const aliveRef = useRef(true)
  useEffect(() => () => { aliveRef.current = false }, [])
  // ★ PROD-822 (review B-r2-2): a result this run did not check (the server's, on resume) is NOT
  //   given the coverage on file's key any more. The Profile autosave, the unmount save and md staff
  //   edits change the member ID without a check while the server keeps last_eligibility, so it may
  //   be an older member ID's. Its key stays unknown: storedResultFor draws nothing for it,
  //   coverageAlreadyChecked does not count it, and the step checks the coverage once (Q-C5, "kein
  //   altes Ergebnis"). Measured at 589f548a: member ID A -> B in Profile, Back; the health-profile
  //   run's insurance step ran no check and showed A's verdict as B's.
  // ★ PROD-822 review REVIEW_PNEXT2 B-1 (cost bound): ...unless the result itself says whose it is.
  //   orella-api stores no inputs key with last_eligibility (measured at 0a07b67), but a 271 that
  //   names its subscriber is stored as subscriberConfirmed { firstName, lastName, dob, memberId }.
  //   When that member ID (and, for a dependent's policy, the policyholder's DOB) matches the
  //   coverage on file, the loaded result is this coverage's (utils/eligibilityLimit.js
  //   loadedResultKey) and the resume pays nothing; a result without it, or one that differs,
  //   stays unknown and is checked once.
  useEffect(() => {
    const st = useOnboardingStore.getState()
    if (st.eligibilityResultKey || !st.insurance.last_eligibility) return
    const k = loadedResultKey(st.insurance)
    if (k) st.setEligibilityResultKey(k)
  }, [])

  useScrollToTop('insurance')

  // Persist insurance (so the endpoint reads the member ID), then run the
  // doctor-independent practice eligibility check. Never blocks Continue.
  const runEligibility = async () => {
    const provider = insurance.provider
    const memberId = insurance.memberId
    if (insurance.selfPay || !userId || !provider || !memberId) return
    if (policyholderPending(insurance)) return // PROD-822 A-1: not even on Re-check without the policyholder's details
    // PROD-696: the coverage this run has paid for, so OnboardingFlow's section save skips it.
    const checkedKey = eligibilityKey(insurance)
    useOnboardingStore.getState().setEligibilityCheckedKey(checkedKey)
    let putSettled = () => {}
    eligibilityPutRef.current = new Promise((resolve) => { putSettled = resolve })
    // ★ PROD-713 review B-S1: this check outlives the step when Continue unmounts it (the PROD-696
    //   blur-then-Continue gesture). The store says it is out, so a step mounted meanwhile (Back)
    //   draws its answer instead of paying for another (inFlightRun, drawEligibilityRun).
    let runSettled = () => {}
    const run = { key: checkedKey, put: eligibilityPutRef.current, done: new Promise((resolve) => { runSettled = resolve }) }
    useOnboardingStore.getState().setEligibilityRun(run)
    if (aliveRef.current) setEligibilityChecking(true)
    try {
      const saved = await profileAPI.update({
        insurance: {
          selfPay: false,
          coverageType: insurance.coverageType || 'commercial-insurance',
          provider, providerId: insurance.providerId, memberId,
          groupNumber: insurance.groupNumber,
          // ★ PROD-822: the policyholder as the step holds it, so the check asks about the policyholder
          //   its key names (measured at 589f548a: a Re-check after a corrected date of birth asked the
          //   payer with the old one). Review REVIEW_PNEXT2 A-1: nothing before a relationship is
          //   chosen and never an empty name or DOB over a saved one (policyholderForWire); the store
          //   now hydrates the policyholder on a resume, so this is the saved row's own.
          ...policyholderForWire(insurance),
          insurerName: provider,
          planType: insurance.coverageType || 'commercial-insurance',
          ...(insurance.discovery_result ? { discovery_result: insurance.discovery_result } : {}),
          // ★ PROD-187 (P12): the payer keys the store holds, so a re-pick reaches the server
          //   whole before the eligibility check reads it (none on a clean store).
          ...payerKeysForWire(insurance),
        },
      })
      putSettled() // the insurance column is written: a waiting section save may go (PROD-696 follow-up)
      // ★ PROD-821 review REVIEW_PNEXT2 C-1: a plan with another payer was picked while this PUT was
      //   out (forgetCoverageCheck marked the run stale): no paid check for the old payer, and its
      //   payer keys are not written back over the new plan's. The plan's own run checks it.
      if (run.stale) return
      // ★ PROD-431 (2), patient push 3a: the server's answer is applied. PUT /profile returns the
      //   stored row after ensure_stedi_payer_id, which is where an ambiguous_region record gets
      //   its regional candidates; first-run onboarding ignored it, so the region card never
      //   showed in that session. Only the payer keys, as the server holds them.
      //   ★ PROD-1190 review M1: not when the coverage changed while the PUT was out (a region tapped
      //   on the scan's region card): the answer is stale and must not undo the pick.
      if (aliveRef.current && saved && saved.insurance && eligibilityKey(useOnboardingStore.getState().insurance) === checkedKey) setInsurance(payerKeysForWire(saved.insurance))
      // PROD-696: through the one reader. A limit answer carries the STORED result (shown as
      //   it is) or null (the card stays as it was).
      // ★ Q-C5 (Alex 2026-10-07, "kein altes Ergebnis"): ...but only for the member ID that
      //   result was checked for. A limit answer for a CHANGED ID shows no verdict: the card and
      //   the stored copy are cleared (the server keeps its own; nothing new is sent), and the
      //   notice says when this ID can be checked.
      const answer = eligibilityAnswer(await profileAPI.runPracticeEligibility(userId))
      const st = useOnboardingStore.getState()
      // ★ PROD-713 review B-S1 (Q-C5): an answer for a coverage the patient has changed since is
      //   neither drawn nor stored; the changed coverage gets its own check.
      // ★ PROD-821: ...nor one whose payer changed under the same key while it was out (stale).
      if (run.stale || eligibilityKey(st.insurance) !== checkedKey) return
      const shown = limitedResultFor(answer, checkedKey, st.eligibilityResultKey)
      st.setEligibilityResultKey(shown.resultKey)
      if (aliveRef.current) setLimitNotice(shown.notice)
      // ★ PROD-713 review B-S1: the store is written whether or not the step is still on screen.
      //   Measured at 40125e7d: the result was stored only while mounted but its key always, so
      //   Continue-then-Back drew no result (and no Re-check), or the old member ID's under the
      //   new ID's key. Only the component state waits for a mounted step.
      if (verdictWithheld(shown.notice)) {
        if (aliveRef.current) setEligibility(null)
        setInsurance({ last_eligibility: null })
      } else if (shown.eligibility) {
        if (aliveRef.current) setEligibility(shown.eligibility)
        setInsurance({ last_eligibility: shown.eligibility })
      }
    } catch (e) {
      /* non-fatal — Continue is never blocked on eligibility */
      // PROD-696: nothing was answered, so the section save may try this coverage once more.
      const st = useOnboardingStore.getState()
      if (!run.stale && st.eligibilityCheckedKey === checkedKey) st.setEligibilityCheckedKey(null) // PROD-821: never a newer check's marker
    } finally {
      // a failed PUT settles here, after the key above was cleared: the section save checks then
      putSettled()
      const st = useOnboardingStore.getState()
      if (st.eligibilityRun === run) st.setEligibilityRun(null)
      runSettled()
      // PROD-821: a stale check ends "checking" only when no newer check is out (the plan's own)
      if (aliveRef.current && !(run.stale && st.eligibilityRun)) setEligibilityChecking(false)
    }
  }
  // ★ PROD-713 review B-S1: the check an earlier mount of this step sent is still out (Continue,
  //   then Back before the answer). This mount shows it checking, waits for the same PUT /profile
  //   (Continue and a secondary save wait on it), and draws the result once it is stored for this
  //   coverage. Nothing is sent.
  const drawEligibilityRun = async (run) => {
    eligibilityPutRef.current = run.put
    if (aliveRef.current) setEligibilityChecking(true)
    await run.done
    if (!aliveRef.current) return
    const st = useOnboardingStore.getState()
    // ★ PROD-713 review r1-B-S1: "checking" ends only when no newer check is out. Measured at
    //   0228ac49: Re-check of ID A out at Continue, Back (this mount draws A's check), the ID
    //   changed to B (B's check out), A answered first -> A's verdict with Re-check for ID B while
    //   B's check was still out, and a tap sent a second concurrent check of B. That newer check's
    //   own `finally` ends "checking" (it belongs to this mount; runEligibility clears the marker
    //   only for its own run).
    if (!st.eligibilityRun) setEligibilityChecking(false)
    const result = storedResultFor(st, run.key)
    if (result && st.eligibilityResultKey === run.key && eligibilityKey(st.insurance) === run.key) setEligibility(result)
  }

  // 1) U2-INS C1 (TEST 3.02): ONE search, with the details the server will read.
  //   * Nothing is asked while a search is in flight.
  //   * `auto` (details entered, card added, "Me"): once per distinct set of details, and never
  //     over coverage the patient already has. The Search button always asks: the server
  //     answers unchanged details for free ('same_inputs', U-5).
  //   * A policyholder who is not the patient needs first name, last name and a full date of
  //     birth (U-1). Incomplete: a sentence, and nothing is sent or paid.
  //   * The saves come first, because the server searches with the SAVED profile: the
  //     identification (non-fatal, as it always was), then the four policyholder keys. If that
  //     save fails, nothing is searched: a paid search on the old details is a wasted one.
  const runSearch = async ({ auto = false, reason = null } = {}) => {
    const ins = useOnboardingStore.getState().insurance
    const idn = useOnboardingStore.getState().identity
    if (!INSURANCE_DISCOVERY_ENABLED || ins.selfPay || !userId || searchingRef.current) return
    const sig = `${needsPolicyholder(ins.subscriberRelationship) ? 'policyholder' : 'self'}|${policyholderSignature(ins)}`
    if (auto && (lastSearchSigRef.current === sig || (ins.provider && ins.memberId))) return
    if (needsPolicyholder(ins.subscriberRelationship) && !policyholderComplete(ins)) {
      setDiscoveryStatus('missing')
      return
    }
    searchingRef.current = true
    skippedRef.current = false
    discoveryFiredRef.current = true
    lastSearchSigRef.current = sig
    setDiscoveryStatus('searching')
    try {
      try {
        await profileAPI.update({ identification: identificationForSearch(idn) })
      } catch (e) { /* non-fatal */ }
      await profileAPI.update({ insurance: policyholderFields(ins) })
      const resp = await profileAPI.runInsuranceDiscovery(userId, { target: 'primary', ...(reason ? { reason } : {}) })
      if (!aliveRef.current || skippedRef.current) return
      const out = discoveryOutcome(resp)
      if (out.searchesLeft !== null) setSearchesLeft(out.searchesLeft)
      if (out.discovery) setInsurance({ discovery_result: out.discovery })
      if (out.kind === 'found') setCandidate(out.discovery.candidates[0])
      if (out.kind === 'failed' || out.kind === 'missing') lastSearchSigRef.current = null
      setDiscoveryStatus(out.kind)
    } catch (e) {
      lastSearchSigRef.current = null
      if (aliveRef.current && !skippedRef.current) setDiscoveryStatus('failed')
    } finally {
      searchingRef.current = false
    }
  }

  // 1b) "Details entered": the policyholder's name and full date of birth are in, and
  //   SEARCH_DEBOUNCE_MS pass with no change. Keyed on the typed details only, never on the
  //   relationship, so the policyholder tap never starts a search (Alex, TEST 3.02).
  const policyholderSig = policyholderSignature(insurance)
  const prevPolicyholderSigRef = useRef(policyholderSig)
  useEffect(() => {
    if (prevPolicyholderSigRef.current === policyholderSig) return
    prevPolicyholderSigRef.current = policyholderSig
    const now = useOnboardingStore.getState().insurance
    if (!needsPolicyholder(now.subscriberRelationship) || !policyholderComplete(now)) return
    const tid = setTimeout(() => { runSearch({ auto: true }) }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(tid)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policyholderSig])

  // The policyholder buttons. "Me" searches at once with the patient's own details (U-6);
  // any other choice only opens the details block.
  const pickPolicyholder = (value) => {
    setInsurance({ subscriberRelationship: value })
    if (value === 'Self') runSearch({ auto: true })
  }

  // 2) Auto eligibility: once carrier + member ID are present (from discovery,
  //    card scan, or manual), fire the check exactly once per coverage.
  //    ★ PROD-696 (P4): a TYPED member ID waits for blur or MEMBER_ID_IDLE_MS with no change
  //    (measured at 53b5ce90: 9 keystrokes = 9 PUT /profile + 9 paid checks). A filled one
  //    (card scan, discovery candidate, region pick) is checked at once, as before.
  const checkEligibilityOnce = () => {
    const key = eligibilityKey(insurance)
    if (insurance.selfPay || !userId || !key) return
    if (lastCheckedKeyRef.current === key) return
    lastCheckedKeyRef.current = key
    // ★ PROD-713: lastCheckedKeyRef starts empty on every mount, so a plain re-mount (Continue
    //   then Back, or the profile run opened again with coverage on file) paid one more check of
    //   an unchanged member ID. The store remembers across mounts: a check sent in this run, or a
    //   stored result checked for this coverage, is reused. Marking it checked also keeps
    //   OnboardingFlow's section save from paying for it. Re-check does not come through here.
    // ★ PROD-822 review REVIEW_PNEXT2 A-1 (P1): a dependent's policy is not checked before the
    //   policyholder's name and full DOB are in; Continue's section save checks it once, with them.
    //   lastCheckedKeyRef is released, so a later change of carrier or member ID is checked.
    if (policyholderPending(insurance)) { lastCheckedKeyRef.current = null; return }
    const st = useOnboardingStore.getState()
    if (coverageAlreadyChecked(st, key)) {
      st.setEligibilityCheckedKey(key)
      // ★ PROD-713 review B-S1: still out (sent by the mount Continue left): draw its answer
      const run = inFlightRun(st, key)
      if (run) eligibilityRunRef.current = drawEligibilityRun(run)
      return
    }
    eligibilityRunRef.current = runEligibility()
  }
  useEffect(() => {
    if (insurance.selfPay || !userId) return
    if (!insurance.provider || !insurance.memberId) return
    if (memberIdTypedRef.current) {
      const tid = setTimeout(() => { memberIdTypedRef.current = false; checkEligibilityOnce() }, MEMBER_ID_IDLE_MS)
      return () => clearTimeout(tid)
    }
    checkEligibilityOnce()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insurance.provider, insurance.memberId, insurance.providerId, insurance.payerId, insurance.selfPay, userId]) // PROD-821: payerId
  const handleMemberIdBlur = () => {
    if (!memberIdTypedRef.current) return
    memberIdTypedRef.current = false
    checkEligibilityOnce()
  }

  const handleScanCapture = (cardData) => {
    if (!cardData) { setScannerSide(null); return }
    const side = scannerSide
    const sideKey = side === 'front' ? 'cardFront' : 'cardBack'
    // Store the image now so the slot shows "captured", but DEFER the auto-fill
    // + payer match until both sides are in (the back carries the payer ID).
    const token = cardPair.beginSide(side) // PROD-1189b
    setInsurance({ [sideKey]: cardData, selfPay: false })
    cardPair.captureSide(side, cardData, token)
    setScannerSide(null)
    // v228.11.7: upload to storage in the background so the camera shot persists
    // (the profile saves the small reference, not base64). Merge when it lands.
    persistCardData(cardData, 'Insurance', side).then((enriched) => {
      if (!cardPair.isCurrent(token)) return // PROD-1189b: removed or replaced meanwhile
      if (enriched && enriched.storagePath) {
        setInsurance({ [sideKey]: enriched })
        cardPair.captureSide(side, enriched, token)
      } else if (enriched && enriched.uploadError) {
        // ★ P2 Release 3: the photo was NOT stored and will not survive the save. Say so.
        alert(cardUploadFailureMessage(t, enriched.uploadError))
      }
    })
  }

  // v228.11.0: upload / drag-drop path — same pipeline as the camera, minus the
  // live-capture modal. OCR runs via the shared helper; result defers to the
  // pair hook exactly like a photo.
  const [uploadingSide, setUploadingSide] = useState(null)
  const uploadSeqRef = useRef(0) // PROD-1189b: the newest upload owns the spinner
  const [showMoreIns, setShowMoreIns] = useState(false)
  // v228.11.3: remove a captured insurance card. Clears that side's image and
  // the pair entry; the other side and any matched carrier/member ID stay.
  const handleRemoveSide = (side) => {
    const sideKey = side === 'front' ? 'cardFront' : 'cardBack'
    setInsurance({ [sideKey]: null })
    cardPair.clearSide(side)
  }

  const handleSideFile = async (side, file) => {
    setUploadingSide(side)
    const seq = ++uploadSeqRef.current
    const token = cardPair.beginSide(side) // PROD-1189b: taken BEFORE the extract starts
    try {
      const cardData = await processCardFile(file, 'Insurance', side, { patientState: identity?.state || '' })
      if (!cardPair.isCurrent(token)) return // PROD-1189b: cleared or replaced while reading: drop it
      const sideKey = side === 'front' ? 'cardFront' : 'cardBack'
      setInsurance({ [sideKey]: cardData, selfPay: false })
      cardPair.captureSide(side, cardData, token)
      if (cardData && cardData.uploadError) alert(cardUploadFailureMessage(t, cardData.uploadError))
    } catch (e) {
      if (!cardPair.isCurrent(token)) return // PROD-1189b: abandoned request: no alert
      alert(e?.message || 'Could not read that image — try another photo.')
    } finally {
      if (uploadSeqRef.current === seq) setUploadingSide(null) // only the newest upload ends the spinner
    }
  }

  // ★ PROD-821 (review B-r2-1): a discovered plan can keep the eligibilityKey of the coverage on
  //   file (the same normalized carrier name and member ID, no registry slug) while it routes the 270
  //   to ANOTHER payerId. What this run checked under that key went to the old payer: forget it, so
  //   the effect below (payerId is one of its dependencies) checks the plan once and no old verdict
  //   is shown. Measured at 589f548a: no check, the old payer's not_found kept (review proof_r2 R2).
  //   The same payerId (the same plan picked again) forgets nothing.
  const forgetCheckOnPayerChange = (before) => {
    const st = useOnboardingStore.getState()
    if (String((before && before.payerId) || '').trim() === String(st.insurance.payerId || '').trim()) return
    lastCheckedKeyRef.current = null
    if (forgetCoverageCheck(st, eligibilityKey(st.insurance)) && aliveRef.current) setEligibility(null)
  }

  const applyCandidate = () => {
    if (!candidate) return
    const before = useOnboardingStore.getState().insurance
    setInsurance({
      selfPay: false,
      ...candidatePayerPick(candidate),
      memberId: candidate.memberIdGuess || '',
      coverageType: insurance.coverageType || 'commercial-insurance',
    })
    forgetCheckOnPayerChange(before)
    setDiscoveryStatus('applied')
  }

  // v242.7.0 (D): index-based apply for the inline pick-list. Applies the chosen
  // candidate (multiple case) rather than always the top match. Returns a result
  // shape so InsuranceCandidateList can await it.
  // ★ INSURE-2b (P12, Alex 9a): it no longer sets 'applied'. That unmounted the list before
  //   its COB stage could render, so onboarding never offered a second plan. The list calls
  //   onDone (finishDiscovery) on every completing path; that is where 'applied' is set.
  const applyCandidateAt = async (idx) => {
    const cands = insurance.discovery_result?.candidates || []
    const cand = cands[idx]
    if (!cand) return { success: false }
    // ★ PROD-187 (P12): the candidate's payer through the owner: payerId = cand.payerId,
    //   stediPayerId / primaryPayerId cleared, providerId its slug or 'custom'.
    const before = useOnboardingStore.getState().insurance
    setInsurance({
      selfPay: false,
      ...candidatePayerPick(cand),
      memberId: cand.memberIdGuess || '',
      coverageType: insurance.coverageType || 'commercial-insurance',
    })
    forgetCheckOnPayerChange(before) // PROD-821
    lastPrimaryIdxRef.current = idx
    return { success: true }
  }

  // ★ INSURE-2b (P12): the second plan, from the list's COB stage. Waits for the eligibility
  //   PUT first, never POSTs a plan without a member ID (the server reads that as "clear the
  //   secondary"), and counts only the server's echo as saved. Any failure is { success: false }.
  const applySecondaryAt = async (idx) => {
    const cand = (insurance.discovery_result?.candidates || [])[idx]
    if (!cand || !userId) return { success: false }
    try { await eligibilityRunRef.current } catch (e) { /* the save below does not depend on it */ }
    const secondary = buildDiscoveredSecondary(cand)
    if (!secondary) return { success: false, error: 'no_member_id' }
    try {
      const result = await profileAPI.saveSecondaryInsurance(userId, secondary)
      if (!secondaryFromResponse(result, secondary).ok) return { success: false, error: 'not_saved' }
      secondaryIdxRef.current = idx
      return { success: true }
    } catch (e) {
      return { success: false, error: 'not_saved' }
    }
  }

  // ★ INSURE-2b (P12): the list's onDone. Stamps the ONBOARDING store's discovery_result (it
  //   lives only there until the final submit) and only then leaves the discovery phase.
  //   "No, just the one plan" saves nothing, so the next 90-day recheck offers it again (D6).
  const finishDiscovery = () => {
    const dr = insurance.discovery_result
    if (dr) {
      setInsurance({
        discovery_result: {
          ...dr,
          acknowledged: true,
          appliedCandidateIndex: lastPrimaryIdxRef.current,
          ...(secondaryIdxRef.current !== null ? { secondaryAppliedIndex: secondaryIdxRef.current } : {}),
          appliedAt: new Date().toISOString(),
        },
      })
    }
    setDiscoveryStatus('applied')
  }

  const skipDiscovery = () => { skippedRef.current = true; setDiscoveryStatus('skipped') }

  const recheckEligibility = () => { lastCheckedKeyRef.current = null; eligibilityRunRef.current = runEligibility() }

  const isValid = insurance.selfPay || (insurance.provider && insurance.memberId)
  const hasFront = !!insurance.cardFront
  const hasBack = !!insurance.cardBack
  const hasCoverage = !!(insurance.provider && insurance.memberId)
  // v228.8.6 (Option A): discovery ran and surfaced nothing (not_found / error).
  // Only show the acknowledgment when discovery actually fired — not on the
  // no-userId early return — and only before the patient has coverage on file.
  // v242.10.0: gated substep — the coverage flow stays closed until the patient
  // picks who the policyholder is. Self-pay bypasses the gate entirely.
  const relSelected = !!insurance.subscriberRelationship
  const isDependent = relSelected && insurance.subscriberRelationship !== 'Self'
  const discoveryNotFound = !insurance.selfPay && discoveryStatus === 'none' &&
    discoveryFiredRef.current && !hasCoverage && !skippedRef.current
  const showDiscoveryPhase = relSelected && !insurance.selfPay && (discoveryStatus === 'searching' || discoveryStatus === 'found')
  const showCardPhase = relSelected && !insurance.selfPay && !showDiscoveryPhase
  const dialogue = insurance.selfPay
    ? t('onboarding.insurance.dialogueSelfPay', "No problem — we'll continue without insurance. You can add it anytime from your profile.")
    : !relSelected
    ? t('onboarding.insurance.dialoguePolicyholder', "Let's start with whose plan this is — I'll grab your card and check coverage right after.")
    : discoveryStatus === 'searching'
    ? t('onboarding.insurance.dialogueSearching', "Let me check if I can find your insurance automatically from your details — one moment.")
    : discoveryStatus === 'found'
    ? t('onboarding.insurance.dialogueFound', "I found a policy that matches you. Confirm it and I'll fill everything in.")
    : t('onboarding.insurance.dialogue', "Scan the front and back of your insurance card, or continue without insurance.")

  return (
    <>
      <OnboardingShell>
        <ScreenHeader onBack={onBack} step={0} total={PROFILE_STEPS} />
        <ScreenIntro
          eyebrow={t('onboarding.insurance.eyebrow', 'Insurance')}
          title={(!relSelected && !insurance.selfPay)
            ? t('onboarding.insurance.titlePolicyholder', "Who's the policyholder?")
            : t('onboarding.insurance.title', 'Add your coverage')}
          dialogue={dialogue}
          required
        />

        <ScrollBody>
          {/* ---- Gate: who's the policyholder? (v242.10.0) ---- */}
          {!insurance.selfPay && (
            <div style={{ marginBottom: showDiscoveryPhase || showCardPhase ? 18 : 4 }}>
              <label style={{ display: 'block', fontSize: 12, color: TEXT_MUTED, marginBottom: 8 }}>
                {t('onboarding.insurance.policyholderQ', 'Are you the primary policyholder on this plan?')}
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                {[
                  { value: 'Self', label: t('profileInsurance2.me', 'Me') },
                  { value: 'Spouse', label: t('profileInsurance2.spouse', 'Spouse') },
                  { value: 'Parent', label: t('profileInsurance2.parent', 'Parent') },
                  { value: 'Child', label: t('profileInsurance2.child', 'Child') },
                  { value: 'Other', label: t('profileInsurance2.other', 'Other') },
                ].map((opt) => {
                  const isSel = insurance.subscriberRelationship === opt.value
                  return (
                    <button
                      key={opt.value}
                      onClick={() => pickPolicyholder(opt.value)}
                      style={{
                        padding: '11px 0', borderRadius: 12, fontSize: 13, fontWeight: 600,
                        border: `1.5px solid ${isSel ? BLUE : BORDER_LIGHT}`,
                        background: isSel ? ICE_BLUE : '#fff',
                        color: isSel ? NAVY : TEXT_HINT,
                        cursor: 'pointer', transition: 'all 0.15s ease',
                      }}
                    >
                      {opt.label}
                    </button>
                  )
                })}
              </div>
              {!relSelected && (
                <p style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: TEXT_HINT, margin: '10px 0 0' }}>
                  <Info size={13} color={TEXT_HINT} />
                  {t('onboarding.insurance.chooseToContinue', 'Choose one to continue')}
                </p>
              )}

              {/* Policyholder's details — dependents only */}
              {isDependent && (
                <div style={{ borderRadius: 14, border: `1.5px solid ${BORDER_LIGHT}`, padding: 14, marginTop: 14 }}>
                  <p style={{ fontSize: 11, fontWeight: 700, color: TEXT_HINT, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>
                    {t('profileInsurance2.policyholderInformation', "Policyholder's details")}
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                    <TextField
                      label={t('profileId.firstName', 'First name')}
                      value={insurance.subscriberFirstName}
                      onChange={(v) => setInsurance({ subscriberFirstName: v })}
                      required={false}
                    />
                    <TextField
                      label={t('profileId.lastName', 'Last name')}
                      value={insurance.subscriberLastName}
                      onChange={(v) => setInsurance({ subscriberLastName: v })}
                      required={false}
                    />
                  </div>
                  {/* TF-01 SWEEP (v242.63.0) — a subscriber's date of birth is a
                      DATE OF BIRTH, so it gets the branded three-column spinner like
                      every other DOB in the app. `type="date"` renders as iOS's
                      native month-grid calendar, which is what "the old date
                      selector" means. v242.61.0 fixed this on ProfileInsurance and
                      MISSED this screen: the report said "the insurance flow" and
                      there are TWO insurance screens. */}
                  <DobPicker
                    label={t('profileId.dob', 'Date of birth')}
                    value={insurance.subscriberDOB}
                    onChange={(v) => setInsurance({ subscriberDOB: v })}
                  />
                </div>
              )}

              {/* ★ U2-INS C1 (TEST 3.02): the explicit search, and what the last one meant. */}
              {INSURANCE_DISCOVERY_ENABLED && relSelected && (
                <div style={{ marginTop: 12 }}>
                  <button
                    type="button"
                    data-search-button="onboarding"
                    onClick={() => runSearch({ reason: 'search' })}
                    disabled={discoveryStatus === 'searching' || searchesLeft === 0}
                    className="w-full font-medium transition"
                    style={{ height: 46, borderRadius: 999, border: `1.5px solid ${BLUE}`, background: '#fff', color: BLUE, fontSize: 14,
                             display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                             opacity: (discoveryStatus === 'searching' || searchesLeft === 0) ? 0.5 : 1,
                             cursor: (discoveryStatus === 'searching' || searchesLeft === 0) ? 'not-allowed' : 'pointer' }}
                  >
                    <Search size={15} />
                    {t('onboarding.insurance.searchButton', 'Search for my insurance')}
                  </button>
                  {searchesLeft !== null && (
                    <p data-searches-left="onboarding" style={{ fontSize: 12, color: TEXT_HINT, textAlign: 'center', margin: '6px 0 0' }}>
                      {t('insuranceSearch.searchesLeft', { defaultValue: '{{count}} of 3 searches left', count: Math.max(0, Math.min(DISCOVERY_CAP, searchesLeft)) })}
                    </p>
                  )}
                  {outcomeMessageKey(discoveryStatus, 'onboarding') && (
                    <p data-search-outcome={discoveryStatus} role="status" style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 12.5, color: NAVY, margin: '10px 0 0', lineHeight: 1.45 }}>
                      <Info size={14} color={BLUE} style={{ flexShrink: 0, marginTop: 1 }} />
                      {t(outcomeMessageKey(discoveryStatus, 'onboarding'))}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ---- Proactive discovery ---- */}
          {showDiscoveryPhase && discoveryStatus === 'searching' && (
            <div
              className="rounded-[16px]"
              style={{ padding: 14, background: '#fff', border: `1.5px solid ${LIGHT_BLUE}`, marginBottom: 14 }}
            >
              <div className="flex items-start gap-2.5">
                <div style={{ width: 36, height: 36, borderRadius: 10, background: ICE_BLUE, border: `1.5px solid ${LIGHT_BLUE}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <OrellaSpinner size={18} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: NAVY }}>
                    {t('onboarding.insurance.searching', 'Searching for your coverage…')}
                  </div>
                  <div style={{ fontSize: 11.5, color: TEXT_MUTED, fontWeight: 500, marginTop: 2 }}>
                    {t('onboarding.insurance.searchingMeta', 'Matching your name & date of birth')}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1.5" style={{ marginTop: 11, fontSize: 11.5, fontWeight: 600, color: BLUE }}>
                <Sparkles size={13} />
                {t('onboarding.insurance.searchingBg', "This runs in the background — you can add your card now if you'd like.")}
              </div>
              <SecondaryButton onClick={skipDiscovery}>
                {t('onboarding.insurance.addCardInstead', 'Add my card instead')}
              </SecondaryButton>
            </div>
          )}

          {showDiscoveryPhase && discoveryStatus === 'found' && (insurance.discovery_result?.candidates?.length > 0) && (
            <div
              className="rounded-[16px]"
              style={{ padding: 14, background: '#fff', border: `1.5px solid ${LIGHT_BLUE}`, marginBottom: 14 }}
            >
              <div className="flex items-start gap-2.5" style={{ marginBottom: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: ICE_BLUE, border: `1.5px solid ${LIGHT_BLUE}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Shield size={18} color={BLUE} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: NAVY }}>
                    {(insurance.discovery_result?.candidates?.length > 1)
                      ? t('onboarding.insurance.foundMatches', 'I found a few plans that could be yours')
                      : t('onboarding.insurance.foundMatch', 'I found coverage that matches you')}
                  </div>
                  <div style={{ fontSize: 11.5, color: TEXT_MUTED, fontWeight: 500, marginTop: 2 }}>
                    {t('onboarding.insurance.foundMeta', 'From your name & date of birth')}
                  </div>
                </div>
              </div>
              {/* v242.7.0 (D): inline pick-list. ★ INSURE-2b (P12, Alex 9a): onboarding
                  offers the second plan too, through the list's COB stage: applySecondaryAt
                  saves it, and finishDiscovery (onDone) closes the discovery phase. */}
              <InsuranceCandidateList
                candidates={insurance.discovery_result?.candidates || []}
                variant="inline"
                singleConfirm={(insurance.discovery_result?.candidates?.length || 0) === 1}
                onApplyPrimary={applyCandidateAt}
                onApplySecondary={applySecondaryAt}
                onDone={finishDiscovery}
                onSkip={skipDiscovery}
              />
            </div>
          )}

          {/* ---- Card + info (manual fallback) ---- */}
          {showCardPhase && (
            <>
              {/* v228.8.6 (Option A): acknowledge a completed discovery that
                  found nothing, instead of silently swapping to the scanner.
                  Auto-hides once the patient starts adding their card. */}
              {discoveryNotFound && !hasFront && !hasBack && (
                <div
                  className="rounded-[16px]"
                  style={{ padding: 14, background: ICE_BLUE, border: `1.5px solid ${LIGHT_BLUE}`, marginBottom: 14 }}
                >
                  <div className="flex items-start gap-2.5">
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: '#fff', border: `1.5px solid ${LIGHT_BLUE}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Info size={18} color={BLUE} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: NAVY }}>
                        {t('onboarding.insurance.notFoundTitle', "I couldn't find your coverage automatically")}
                      </div>
                      <div style={{ fontSize: 12, color: TEXT_MUTED, fontWeight: 500, marginTop: 2 }}>
                        {t('onboarding.insurance.notFoundMeta', 'No problem — scan your card or enter your details below.')}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3" style={{ marginBottom: 14 }}>
                <CardSlot
                  captured={insurance.cardFront}
                  onCamera={() => { setInsurance({ selfPay: false }); setScannerSide('front') }}
                  onFile={(f) => handleSideFile('front', f)}
                  onRemove={() => handleRemoveSide('front')}
                  busy={uploadingSide === 'front'}
                  label={t('onboarding.insurance.cardFront', 'Front')}
                />
                <CardSlot
                  captured={insurance.cardBack}
                  onCamera={() => { setInsurance({ selfPay: false }); setScannerSide('back') }}
                  onFile={(f) => handleSideFile('back', f)}
                  onRemove={() => handleRemoveSide('back')}
                  busy={uploadingSide === 'back'}
                  label={t('onboarding.insurance.cardBack', 'Back')}
                />
              </div>

              {/* PROD-936 PAT-2 (NEW-2, Alex 2026-10-13 Q47): with no discovery the card is the way in, so
                  say where the carrier and member ID are. Under es nothing until Dr. Rami approves. */}
              {!hasFront && !hasBack && !insurance.provider && hasText('onboarding.insurance.cardHint') && (
                <p data-card-hint style={{ fontSize: 12, color: TEXT_MUTED, fontWeight: 500, margin: '-4px 2px 14px' }}>
                  {t('onboarding.insurance.cardHint')}
                </p>
              )}

              {/* v228.8.6: one side captured — auto-fill + payer match wait for
                  both, so prompt for the remaining side. */}
              {((hasFront && !hasBack) || (hasBack && !hasFront)) && !insurance.provider && (
                <div
                  className="flex items-center gap-2 rounded-[14px]"
                  style={{ padding: '11px 13px', background: ICE_BLUE, border: `1.5px solid ${LIGHT_BLUE}`, marginBottom: 14 }}
                >
                  <OrellaSpinner size={16} />
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: NAVY }}>
                    {hasFront
                      ? t('onboarding.insurance.addBackToFinish', 'Now add the back — I read both sides together to match your plan.')
                      : t('onboarding.insurance.addFrontToFinish', 'Now add the front — I read both sides together to match your plan.')}
                  </span>
                </div>
              )}

              {((hasFront && hasBack) || insurance.provider) && (
                <div
                  className="rounded-[14px]"
                  style={{ padding: 14, background: '#fff', border: `1.5px solid ${BORDER_LIGHT}`, marginBottom: 14 }}
                >
                  {/* v228.9.0 (CF-2): the payer was an auto-matched best guess
                      (no payer ID on the card). Eligibility runs on it, but ask
                      the patient to confirm before it's used for billing. */}
                  {/* ★ L1 step 6 / D4 (PROD-188): a resumed onboarding can hydrate a record the
                      server stored with no payer, only the regional siblings of the carrier
                      name. There is nothing to confirm; the patient picks the region. */}
                  {regionChoices(insurance) ? (
                    <div data-region-pick="onboarding" style={{ marginBottom: 12, padding: '12px', background: ICE_BLUE, border: `1.5px solid ${LIGHT_BLUE}`, borderRadius: 12 }}>
                      <div style={{ fontSize: 12.5, color: TEXT_MUTED, fontWeight: 600, marginBottom: 8 }}>
                        {t('onboarding.insurance.pickRegionPrompt', 'Your insurance has plans for more than one region. Which one is on your card?')}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {regionChoices(insurance).map((c) => (
                          <button
                            key={c.payerId}
                            onClick={() => setInsurance({ ...regionPick(c) })}
                            className="w-full text-left"
                            style={{ padding: '9px 11px', borderRadius: 10, background: '#fff',
                                     border: `1px solid ${BORDER_LIGHT}`, fontSize: 13, fontWeight: 600, color: NAVY, cursor: 'pointer' }}
                          >
                            {c.displayName}
                          </button>
                        ))}
                      </div>
                      <button
                        onClick={() => setCarrierSheetOpen(true)}
                        style={{ marginTop: 10, width: '100%', height: 40, borderRadius: 999, background: '#fff', color: NAVY, fontSize: 13.5, fontWeight: 600, border: `1.5px solid ${BORDER_LIGHT}`, cursor: 'pointer' }}
                      >
                        {t('onboarding.insurance.confirmPlanChange', 'Pick a different one')}
                      </button>
                    </div>
                  ) : insurance.payerNeedsConfirmation && !regionSettled(insurance) && (insurance.stediPayerName || insurance.provider) && (
                    <div style={{ marginBottom: 12, padding: '12px', background: ICE_BLUE, border: `1.5px solid ${LIGHT_BLUE}`, borderRadius: 12 }}>
                      <div style={{ fontSize: 12.5, color: TEXT_MUTED, fontWeight: 600, marginBottom: 5 }}>
                        {t('onboarding.insurance.confirmPlanPrompt', "We matched your card to this plan — is it right?")}
                      </div>
                      <div className="flex items-center gap-2" style={{ marginBottom: 10 }}>
                        <Shield size={16} color={BLUE} />
                        <span style={{ fontSize: 14.5, fontWeight: 700, color: NAVY }}>
                          {insurance.stediPayerName || insurance.provider}
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setInsurance({ payerNeedsConfirmation: false })}
                          style={{ flex: 1, height: 40, borderRadius: 999, background: BLUE, color: '#fff', fontSize: 13.5, fontWeight: 600, border: 'none', cursor: 'pointer' }}
                        >
                          {t('onboarding.insurance.confirmPlanYes', "Yes, that's my plan")}
                        </button>
                        <button
                          onClick={() => setCarrierSheetOpen(true)}
                          style={{ flex: 1, height: 40, borderRadius: 999, background: '#fff', color: NAVY, fontSize: 13.5, fontWeight: 600, border: `1.5px solid ${BORDER_LIGHT}`, cursor: 'pointer' }}
                        >
                          {t('onboarding.insurance.confirmPlanChange', 'Pick a different one')}
                        </button>
                      </div>
                      {/* v242.64.0 (TF-11): the server ranks up to 5 alternates and
                          sends them as payerCandidates. Nothing had ever rendered
                          them, so "Pick a different one" led only to a live search
                          that cannot answer when the payer directory is down. */}
                      {Array.isArray(insurance.payerCandidates) && insurance.payerCandidates.length > 1 && (
                        <div style={{ marginTop: 12 }}>
                          <div style={{ fontSize: 11.5, fontWeight: 600, color: TEXT_MUTED, marginBottom: 6 }}>
                            {t('onboarding.insurance.otherMatches', 'Other close matches')}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {insurance.payerCandidates
                              .filter((c) => c && c.displayName && c.displayName !== (insurance.stediPayerName || insurance.provider))
                              .slice(0, 4)
                              .map((c) => (
                                <button
                                  key={c.payerId || c.displayName}
                                  onClick={() => setInsurance({
                                    ...payerPick({ kind: 'alternate', payerId: c.payerId, name: c.displayName, eligibility: c.eligibility }),
                                  })}
                                  className="w-full text-left"
                                  style={{ padding: '9px 11px', borderRadius: 10, background: '#fff',
                                           border: `1px solid ${BORDER_LIGHT}`, fontSize: 13, color: NAVY, cursor: 'pointer' }}
                                >
                                  {c.displayName}
                                </button>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* v228.11.0: minimal kept fields — carrier + member ID. No green
                      box, no field dump. Group/coverage sit behind "Edit details". */}
                  <div className="mb-3">
                    <label className="block font-medium mb-1.5" style={{ fontSize: 13, color: NAVY }}>
                      {t('onboarding.insurance.carrier', 'Carrier')} <span style={{ color: '#DC2626' }}>*</span>
                      {/* v242.64.0 (TF-11): this read "matched from card" for a
                          0.53 fuzzy NAME guess. It may only claim the card as its
                          source when nothing is still awaiting confirmation. */}
                      {insurance.providerId && insurance.providerId !== 'custom'
                        && !insurance.payerNeedsConfirmation && (
                        <span
                          className="ml-1.5 inline-flex items-center font-medium"
                          style={{ fontSize: 11, padding: '2px 7px', borderRadius: 999, background: ICE_BLUE, color: BLUE }}
                        >
                          {t('onboarding.insurance.matchedFromCard', 'matched from card')}
                        </span>
                      )}
                    </label>
                    <button
                      onClick={() => setCarrierSheetOpen(true)}
                      className="w-full text-left transition"
                      style={{
                        height: 46, padding: '0 12px', borderRadius: 11,
                        border: `1.5px solid ${BORDER_LIGHT}`, background: '#fff',
                        color: insurance.provider ? NAVY : TEXT_HINT,
                        fontSize: 15, cursor: 'pointer',
                      }}
                    >
                      {insurance.provider || t('onboarding.insurance.pickCarrier', 'Tap to pick carrier')}
                    </button>
                  </div>

                  <TextField
                    label={t('onboarding.insurance.memberId', 'Member ID')}
                    value={insurance.memberId}
                    onChange={(v) => { memberIdTypedRef.current = true; setInsurance({ memberId: v }) }}
                    onBlur={handleMemberIdBlur}
                    placeholder="XQA945127"
                    prefilled={!!insurance.memberId}
                  />

                  {showMoreIns ? (
                    <TextField
                      label={t('onboarding.insurance.group', 'Group')}
                      value={insurance.groupNumber}
                      onChange={(v) => setInsurance({ groupNumber: v })}
                      placeholder="0042-001"
                      required={false}
                      prefilled={!!insurance.groupNumber}
                    />
                  ) : (
                    <button
                      onClick={() => setShowMoreIns(true)}
                      style={{ fontSize: 12.5, fontWeight: 600, color: BLUE, background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0' }}
                    >
                      {t('onboarding.insurance.editDetails', 'Edit details')}
                    </button>
                  )}

                  {/* ★ INSURE-1 (P12, Alex 9b): where a second or third plan goes. */}
                  {hasCoverage && !insurance.selfPay && (
                    <p style={{ fontSize: 12, color: TEXT_MUTED, lineHeight: 1.5, margin: '10px 0 0' }}>
                      {t('onboarding.insurance.moreCoverageHint', 'Have more than one plan? You can add further coverage anytime under Profile → Insurance.')}
                    </p>
                  )}

                </div>
              )}
            </>
          )}

          {/* ---- Auto eligibility result ---- */}
          {/* PROD-431 item 91: over the region card, no "Setting up" (payer_not_mapped) badge */}
          {/* Q-C5: a changed member ID at the limit draws only the notice (no verdict) */}
          {hasCoverage && !insurance.selfPay && (eligibility || eligibilityChecking || verdictWithheld(limitNotice)) && !badgeHiddenByRegionCard(insurance, eligibility) && (
            <div style={{ marginBottom: 4 }}>
              <EligibilityBadge
                eligibility={eligibility}
                variant="detailed"
                loading={eligibilityChecking}
                onRecheck={recheckEligibility}
                limitNotice={noticeForCoverage(limitNotice, insurance) /* PROD-936: a reuse notice holds only for its coverage */}
                onRescanCard={() => { setInsurance({ selfPay: false }); setScannerSide('front') }}
              />
            </div>
          )}

          {/* ---- s106 (canvas "Orella Referrals Screens" A0): an HMO-type plan's primary care doctor, the same
              block as Profile › Insurance (D497): the card's PCP to confirm, or pick one. ---- */}
          {hasCoverage && !insurance.selfPay && pcpNeededFor(insurance.coverageType) && (
            <div style={{ marginBottom: 14 }} data-testid="onboarding-pcp">
              <p style={{ fontSize: 13, color: TEXT_MUTED, lineHeight: 1.5, marginBottom: 8 }}>
                {t('referral.onboardingPcpNote', "HMO plans usually start with your primary care doctor. We'll suggest them first when you send a case.")}
              </p>
              <PrimaryCareDoctor value={insurance.pcp || null} cardPcp={insurance.pcpFromCard || null} coverageType={insurance.coverageType}
                zip={identity?.zipCode || ''} onChange={(pcp) => setInsurance({ pcp })} t={t} />
            </div>
          )}

          {/* ---- Self-pay confirmation (one owner, also the before-share block's: PROD-1132) ---- */}
          {insurance.selfPay && <SelfPayConfirmedNote />}
        </ScrollBody>

        <Footer>
          <PrimaryButton onClick={() => onNext(eligibilityPutRef.current)} disabled={!isValid}>
            {t('common.continue', 'Continue')}
          </PrimaryButton>
          {!insurance.selfPay && (
            <SecondaryButton
              onClick={() => setInsurance({ selfPay: true, ...payerPick({ kind: 'clear' }), memberId: '', groupNumber: '', coverageType: 'self-pay' })}
            >
              {t('onboarding.insurance.selfPay', "Continue without insurance")}
            </SecondaryButton>
          )}
          {/* PROD-1132 (Alex 2026-10-13, Q36/Q47): "I have insurance, not at hand". Saves the section NOT
              complete (no eligibility check: nothing to check); the card is added any time in Profile >
              Insurance. Not shown with a card on screen or under self-pay; under es until Dr. Rami approves. */}
          {!insurance.selfPay && !hasCoverage && hasText('onboarding.insurance.cardLater') && (
            <SecondaryButton onClick={() => onNext(eligibilityPutRef.current, { complete: false })}>
              {t('onboarding.insurance.cardLater')}
            </SecondaryButton>
          )}
          {!insurance.selfPay && !hasCoverage && hasText('onboarding.insurance.cardLater') && hasText('onboarding.insurance.cardLaterHint') && (
            <p data-card-later-hint style={{ fontSize: 12, color: TEXT_MUTED, fontWeight: 500, margin: '2px 2px 0', textAlign: 'center' }}>
              {t('onboarding.insurance.cardLaterHint')}
            </p>
          )}
          {insurance.selfPay && (
            <SecondaryButton
              onClick={() => { skippedRef.current = false; discoveryFiredRef.current = false; setInsurance({ selfPay: false, coverageType: '' }); setDiscoveryStatus('idle') }}
            >
              {t('onboarding.insurance.changedMind', 'I have insurance after all')}
            </SecondaryButton>
          )}
        </Footer>
      </OnboardingShell>

      {scannerSide && (
        <CardScanner
          cardType="insurance"
          side={scannerSide}
          onCapture={handleScanCapture}
          onCancel={() => setScannerSide(null)}
          onUpload={(file) => { const s = scannerSide; setScannerSide(null); handleSideFile(s, file) }}
          enableOCR={true}
          patientState={identity?.state || ''}
        />
      )}

      {carrierSheetOpen && (
        <CarrierSearchSheet
          onPick={(p) => {
            const pid = p.primaryPayerId || p.stediId || ''
            // ★ PROD-187 (P12): the whole payer key set, through the owner; onboarding keeps
            //   its primary-first id order. v242.64.0 (TF-11) still holds: a hand-typed
            //   carrier is the patient's own words, not a resolved payer, so the typed kind
            //   stamps no 'high' confidence and carries no routable id anywhere. Either way
            //   it is an explicit patient choice, so nothing asks to confirm it again.
            setInsurance(pid
              ? payerPick({ kind: 'registry', stediId: p.stediId, primaryPayerId: p.primaryPayerId, name: p.displayName, order: 'primary-first', source: 'patient_pick', eligibility: p.eligibility })
              : payerPick({ kind: 'typed', name: p.displayName }))
          }}
          onClose={() => setCarrierSheetOpen(false)}
        />
      )}
    </>
  )
}
